import "./ProfileDrawer.css";
import {
  Button,
  Divider,
  Grid,
  Hidden,
  IconButton,
  Slider,
  SwipeableDrawer,
  Tooltip,
  Typography,
} from "@material-ui/core";
import signOutButton from "../../assets/icons/signOutIcon.png";
import FirebaseService from "../../services/firebase/FirebaseService";
import info from "../../assets/icons/noun_Information_1415287.png";
import microphoneIcon from "../../assets/icons/noun_Microphone_1234740.png";
import { RootState } from "../../state/configureStore";
import { connect } from "react-redux";
import AudioDeviceSelector from "./AudioDeviceSelector";
import useModal from "../Modals/UseModal";
import JoinClassroomModal from "../Modals/JoinClassroomModal";
import LogService from "../../services/LogService";
import add from "../../assets/icons/noun_plus.svg";

const FontSizeSlider = ({ uid, val }: { uid: string; val: number }) => {
  return (
    <div className="slider">
      <Slider
        marks
        value={val ?? -3}
        step={3}
        min={-3}
        max={3}
        onChangeCommitted={(_event, val) => {
          FirebaseService.updateUserSettings(uid, "globalFontSize", val).catch(err => {
            LogService.exception(err, `ProfileDrawer.FontSizeSlider`, "Error updating user profile");
          });
        }}
      />
    </div>
  );
};

const ReaderSizeSlider = ({ uid, val }: { uid: string; val: number }) => {
  return (
    <div className="slider">
      <Slider
        marks
        value={val ?? 12}
        step={4}
        min={12}
        max={48}
        onChangeCommitted={(_event, val) => {
          FirebaseService.updateUserSettings(uid, "readerFontSize", val);
        }}
      />
    </div>
  );
};

const signOut = async () => {
  await FirebaseService.logout();
};

const Header = () => (
  <Typography variant="h4" className="header-text">
    User Settings
  </Typography>
);

const JoinClassroomButton = ({ isStudent, openModal }: { isStudent: boolean; openModal: () => void }) => {
  if (!isStudent) return null;

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "15px" }}>
      <Button
        startIcon={<img style={{ color: "#f5f2ed" }} height={20} src={add} alt="Add Students" />}
        variant="contained"
        color="primary"
        onClick={openModal}
      >
        <p className="join-class">Join Classroom</p>
      </Button>
    </div>
  );
};

const Footer = () => (
  <div>
    <IconButton onClick={signOut} className="signOut">
      <img className="IconImage" src={signOutButton} alt="Sign Out" height={30} color="black" />
      <Typography variant="body1">Sign Out</Typography>
    </IconButton>
    <br />
    <Typography variant="caption">
      <a href="https://www.readlee.com/privacy-policy" target="_blank" rel="noopener noreferrer">
        Terms of Agreement
      </a>
      {" \u2022 "}Need Help? Contact{" "}
      <a href="mailto:info@readlee.com" target="_top">
        info@readlee.com
      </a>
    </Typography>
  </div>
);

interface PassedProps {
  onClose: () => void;
  onOpen: () => void;
  open: boolean;
}

type ProfileDrawerProps = ReturnType<typeof mapStateToProps> & PassedProps;

const ProfileDrawer = (props: ProfileDrawerProps) => {
  const { open, openModal, closeModal } = useModal(false);
  const { profile, settings, onClose, onOpen, open: drawerOpen } = props;

  if (!profile) return null;

  const isStudent = profile.userType === "student";
  return (
    <>
      <SwipeableDrawer disableSwipeToOpen onClose={onClose} onOpen={onOpen} open={drawerOpen} anchor="right">
        <Hidden xsDown>
          <div className="profileDrawer">
            <div>
              <Header />
              <JoinClassroomButton isStudent={isStudent} openModal={openModal} />

              <Grid container className="settings">
                <Grid item xs={3}>
                  <Typography variant="body1">Reader Text Size</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Tooltip title="Determines size of text inside reader view">
                    <img src={info} alt="Text Size" height={23} />
                  </Tooltip>
                </Grid>
                <Grid item xs={1}>
                  <Typography variant="body1">{settings?.readerFontSize ?? "12"}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <ReaderSizeSlider val={settings?.readerFontSize ?? 12} uid={profile.uid} />
                </Grid>
              </Grid>
              <Divider />
              <Grid container className="settings">
                <Grid item xs={3}>
                  <Typography variant="body1">Microphone</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Tooltip title="Select the audio device that you will use when recording your voice.">
                    <img src={info} alt="Microphone" height={23} />
                  </Tooltip>
                </Grid>
                <Grid item xs={1}>
                  <img src={microphoneIcon} alt="Microphone" height={23} />
                </Grid>
                <Grid item xs={4}>
                  <AudioDeviceSelector />
                </Grid>
              </Grid>
              <Divider className="divider" />
            </div>

            <Footer />
          </div>
        </Hidden>
        <Hidden smUp>
          <div className="mobileProfileDrawer">
            <div>
              <Header />
              <JoinClassroomButton isStudent={isStudent} openModal={openModal} />

              <div className="textRow">
                <Typography variant="body1">Overall Text Size</Typography>
              </div>

              <div className="textRow">
                <Typography variant="body1">
                  {settings?.globalFontSize === undefined
                    ? "Small"
                    : settings?.globalFontSize === -3
                    ? "Small"
                    : settings?.globalFontSize === 0
                    ? "Med"
                    : settings?.globalFontSize === 3
                    ? "Large"
                    : "Small"}
                </Typography>

                <FontSizeSlider val={settings?.globalFontSize ?? -3} uid={profile.uid} />
              </div>
              <Divider className="divider" />

              <div className="textRow">
                <Typography variant="body1">Reader Text Size</Typography>
              </div>

              <div className="textRow">
                <Typography variant="body1">{settings?.readerFontSize ?? "12"}</Typography>
                <ReaderSizeSlider val={settings?.readerFontSize ?? 12} uid={profile.uid} />
              </div>
              <Divider className="divider" />

              <div className="textRow">
                <Typography variant="body1" style={{ padding: 5 }}>
                  Microphone
                </Typography>
                <img src={microphoneIcon} alt="Microphone" height={23} />
              </div>

              <AudioDeviceSelector />
            </div>
            <Footer />
          </div>
        </Hidden>
      </SwipeableDrawer>
      <JoinClassroomModal open={open} closeModal={closeModal} />
    </>
  );
};
const mapStateToProps = (state: RootState) => {
  return {
    profile: state.currentUserState.currentUser,
    settings: state.currentUserState.currentUserSettings,
  };
};

export default connect(mapStateToProps)(ProfileDrawer);
